import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import { IconButton, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Bouton d'installation PWA.
 * - Chromium (Chrome/Edge/…) : `beforeinstallprompt` est capté très tôt dans
 *   index.tsx (avant React) et stocké sur `window.__deferredInstallPrompt` ;
 *   ce composant le récupère (au mount + via l'event `pwa-installable`) puis
 *   appelle `.prompt()` au clic → bouton visible et fiable.
 * - App déjà installée / mode standalone : bouton masqué.
 * - Firefox desktop / Safari : `beforeinstallprompt` n'existe pas (limite
 *   navigateur) → aucun bouton, l'install se fait manuellement via le menu.
 */
export const InstallPwaButton = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(
    (window as any).__deferredInstallPrompt || null,
  );
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone) {
      setInstalled(true);
    }

    // event capté tôt (index.tsx) → l'event a peut-être déjà eu lieu
    if ((window as any).__deferredInstallPrompt) {
      setDeferredPrompt((window as any).__deferredInstallPrompt);
    }

    const onInstallable = () => {
      setDeferredPrompt((window as any).__deferredInstallPrompt || null);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('pwa-installable', onInstallable);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('pwa-installable', onInstallable);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    // le prompt n'est utilisable qu'une fois
    (window as any).__deferredInstallPrompt = null;
    setDeferredPrompt(null);
  };

  // Rien à proposer : déjà installée, ou navigateur sans support (Firefox/Safari)
  if (installed || !deferredPrompt) {
    return null;
  }

  return (
    <Tooltip title={t('pwa.install')}>
      <IconButton
        onClick={handleClick}
        color={'primary'}
        aria-label={t('pwa.install')!}
        sx={{ mr: 1 }}
      >
        <InstallDesktopIcon />
      </IconButton>
    </Tooltip>
  );
};
